import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import { getAllEmployees } from '../../services/api';

const employeeListParams = (department, designation, status) => ({
  department: department || undefined,
  designation: designation || undefined,
  status: status || undefined,
});

export default function EmployeeSearchField({
  value,
  emptyValue,
  onChange,
  label,
  placeholder,
  apiFilters,
  selectedDisplay,
  debounceMs,
}) {
  const isEmpty = value === emptyValue || value === null || value === undefined;
  const { department, designation, status } = apiFilters || {};

  const [searchInput, setSearchInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isEmpty) return;
    setSelectedEmployee(null);
    setSearchInput('');
    setOptions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [isEmpty]);

  const search = useCallback(
    async (query) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!query || query.trim().length < 1) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await getAllEmployees({
            search: query,
            ...employeeListParams(department, designation, status),
          });
          setOptions(res.data.results || []);
        } catch (err) {
          console.error('Error searching employees:', err);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceMs);
    },
    [department, designation, status, debounceMs]
  );

  const handleSelect = useCallback(
    (employee) => {
      if (!employee) return;
      setSelectedEmployee(employee);
      onChange(employee.id);
      setSearchInput('');
      setOptions([]);
      setLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setSelectedEmployee(null);
    setSearchInput('');
    setOptions([]);
    setLoading(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange(emptyValue);
  }, [emptyValue, onChange]);

  if (!isEmpty) {
    const display =
      selectedEmployee?.full_name && selectedEmployee?.employee_id
        ? `${selectedEmployee.full_name}  |  ${selectedEmployee.employee_id}`
        : selectedDisplay || `${value}`;

    return (
      <TextField
        label={label}
        size="small"
        fullWidth
        value={display}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon fontSize="small" color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} edge="end" title="Clear selection">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            bgcolor: 'action.selected',
            '& input': { fontWeight: 500 },
          },
        }}
      />
    );
  }

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => `${option.full_name} | ${option.employee_id}`}
      value={null}
      inputValue={searchInput}
      onInputChange={(event, nextValue, reason) => {
        if (reason === 'reset') return;
        setSearchInput(nextValue);
        search(nextValue);
      }}
      onChange={(event, nextValue) => {
        if (nextValue) handleSelect(nextValue);
      }}
      loading={loading}
      noOptionsText={
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : searchInput.trim().length > 0 ? (
          'No employees match your search'
        ) : (
          'No employees'
        )
      }
      filterOptions={(x) => x}
      clearOnBlur={false}
      blurOnSelect
      openOnFocus
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="body2" fontWeight={500}>
              {option.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.employee_id}
            </Typography>
          </Box>
        </li>
      )}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
    />
  );
}

EmployeeSearchField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  emptyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  apiFilters: PropTypes.shape({
    department: PropTypes.string,
    designation: PropTypes.string,
    status: PropTypes.string,
  }),
  selectedDisplay: PropTypes.string,
  debounceMs: PropTypes.number,
};

EmployeeSearchField.defaultProps = {
  emptyValue: '',
  label: 'Employee',
  placeholder: 'Type to search employees…',
  apiFilters: undefined,
  selectedDisplay: undefined,
  debounceMs: 450,
};
